<template>
	<mgl-geo-json-source source-id="mapvision-zones" :data="zonesFC">
		<mgl-line-layer layer-id="mapvision-zone-outline" :paint="linePaint" />
	</mgl-geo-json-source>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { MglGeoJsonSource, MglLineLayer } from "vue-maplibre-gl";
import type { ExpressionSpecification } from "maplibre-gl";
import { apiClient } from "@/client";
import type { GeoJSON } from "geojson";

interface MapvisionZoneFeature {
	type: "Feature";
	properties: {
		zone_id: number;
		name?: string | null;
		district?: string | null;
		color?: string | null;
		site_count?: number | null;
	};
	geometry: GeoJSON.GeoJSON;
}

const emptyFC: GeoJSON.FeatureCollection = {
	type: "FeatureCollection",
	features: [],
};
const zonesFC = ref<GeoJSON.FeatureCollection>(emptyFC);

const features = computed<MapvisionZoneFeature[]>(
	() => (zonesFC.value.features as MapvisionZoneFeature[]) ?? [],
);

// Stroke each zone with the color MapVision assigned it so the zones read as
// distinct areas; fall back to a single outline color when none are set.
// MapVision colors are sometimes malformed (e.g. "#50S2G0"), so only colors
// that parse as CSS hex make it into the match expression — anything invalid
// falls through to the default outline color instead of breaking the layer.
const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;

function isValidColor(c: string): boolean {
	return HEX_COLOR.test(c.trim());
}

const linePaint = computed(() => {
	const colors = new Set<string>();
	for (const f of features.value) {
		const c = f.properties.color;
		if (c && isValidColor(c)) colors.add(c.trim());
	}
	if (colors.size === 0) {
		return { "line-color": "#0d6efd", "line-width": 2, "line-opacity": 0.9 };
	}
	const expr: unknown[] = ["match", ["get", "color"]];
	colors.forEach((c) => expr.push(c, c));
	expr.push("#0d6efd");
	return {
		"line-color": expr as ExpressionSpecification,
		"line-width": 2,
		"line-opacity": 0.9,
	};
});

onMounted(async () => {
	try {
		const collection = await apiClient.JSONGet<GeoJSON.FeatureCollection>(
			"/api/mapvision/zone",
		);
		if (collection) zonesFC.value = collection;
	} catch {
		// Leave the map without zone outlines if the endpoint is unavailable.
	}
});
</script>
